import { Box, Grid, Typography, useTheme } from '@hermes/web-components'
import { useContext, useMemo } from 'react'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import highlight from '../../../../utils/highlightText'
import { RootContainer, ItemRootContainer, ItemTitleContainer, ItemLogo, ItemTitle, ItemSubTitle } from './styles'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { AppDataContext } from '../../../../providers/AppData'

export interface Item {
  id: number
  name: string
  logo?: string
  subtitle?: string
  onSelect: (...props: any) => void
  slug: string
}

interface ResultsListItemProps extends Item {
  specialistOrPractice: boolean
  searchString: string
  slug: string
}

interface ResultsListProps {
  items?: Array<Item>
  specialties?: Array<Item>
  conditionsProcedures?: Array<Item>
  specialistOrPractice?: boolean
  searchString: string
  bubbleView?: boolean
}

const ResultsListItemOld = ({
  name,
  logo,
  subtitle,
  specialistOrPractice = false,
  onSelect,
  searchString
}: ResultsListItemProps) => (
  <ItemRootContainer onClick={onSelect}>
    {specialistOrPractice && logo && <ItemLogo src={logo} alt={name} />}
    <ItemTitleContainer>
      <ItemTitle
        specialistOrPractice={specialistOrPractice && !searchString}
        dangerouslySetInnerHTML={{ __html: highlight(name, searchString) }}
      />
      {specialistOrPractice && subtitle ? <ItemSubTitle>{subtitle}</ItemSubTitle> : null}
    </ItemTitleContainer>
  </ItemRootContainer>
)

const ResultsListItem = ({ name, onSelect, slug }: ResultsListItemProps) => {
  const { language } = useContext(AppDataContext)
  const {
    buildUrl,
    state: { currentLocation }
  } = useContext(SearchProfilesPageContext)
  const url = buildUrl({
    keywordSlug: slug,
    slug: currentLocation?.slug,
    location: currentLocation?.name[language],
    procedureConditionSlug: null
  })
  return (
    <div>
      <a href={url} onClick={onSelect}>
        <span>{name}</span>
      </a>
    </div>
  )
}

const ResultsList = ({
  items = [],
  specialties = [],
  conditionsProcedures = [],
  specialistOrPractice = false,
  searchString,
  bubbleView = false
}: ResultsListProps) => {
  const translate = useTranslateMessage()
  const theme = useTheme()

  const reorderedConditionsProcedures = useMemo(() => {
    if (bubbleView) {
      return conditionsProcedures.sort((a, b) => {
        if (a.name < b.name) {
          return -1
        }
        if (a.name > b.name) {
          return 1
        }
        return 0
      })
    }
    return conditionsProcedures
  }, [conditionsProcedures, bubbleView])

  const reorderedSpecialties = useMemo(() => {
    if (bubbleView) {
      return specialties.sort((a, b) => {
        if (a.name < b.name) {
          return -1
        }
        if (a.name > b.name) {
          return 1
        }
        return 0
      })
    }
    return specialties
  }, [specialties, bubbleView])

  const groupedConditionsProceduresByFirstLetter = useMemo(
    () =>
      reorderedConditionsProcedures.reduce((groups: Record<string, Array<Item>>, item) => {
        const firstLetter = item.name.charAt(0).toUpperCase()

        if (!groups[firstLetter]) {
          groups[firstLetter] = []
        }

        groups[firstLetter].push(item)

        return groups
      }, {}),
    [reorderedConditionsProcedures]
  )

  const groupedSpecialtiesByFirstLetter = useMemo(
    () =>
      reorderedSpecialties.reduce((groups: Record<string, Array<Item>>, item) => {
        const firstLetter = item.name.charAt(0).toUpperCase()

        if (!groups[firstLetter]) {
          groups[firstLetter] = []
        }

        groups[firstLetter].push(item)

        return groups
      }, {}),
    [reorderedSpecialties]
  )

  if (items.length) {
    return (
      <RootContainer>
        {items.map((item: Item) => (
          <ResultsListItemOld
            key={`search-${item.subtitle}-${item.id}`}
            specialistOrPractice={specialistOrPractice}
            searchString={searchString}
            {...item}
          />
        ))}
      </RootContainer>
    )
  }

  return (
    <RootContainer>
      {reorderedConditionsProcedures.length ? (
        <>
          <Typography variant="body2" sx={{ px: 2 }}>
            {translate('popular_conditions_procedures')}
          </Typography>
          <Grid sx={{ ...(theme?.custom?.SearchBar?.resultsList ?? {}) }}>
            {bubbleView ? (
              <Box display="contents">
                {Object.keys(groupedConditionsProceduresByFirstLetter).map((key, index) => (
                  <Box display="contents" key={index}>
                    <Box style={{ marginTop: '8px' }}>{key}</Box>
                    {groupedConditionsProceduresByFirstLetter[key].map((item) => (
                      <ResultsListItem
                        key={`search-${item.subtitle}-${item.id}`}
                        specialistOrPractice={specialistOrPractice}
                        searchString={searchString}
                        {...item}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            ) : (
              conditionsProcedures.map((item: Item) => (
                <ResultsListItem
                  key={`search-${item.subtitle}-${item.id}`}
                  specialistOrPractice={specialistOrPractice}
                  searchString={searchString}
                  {...item}
                />
              ))
            )}
          </Grid>
        </>
      ) : null}
      {reorderedSpecialties.length ? (
        <>
          <Typography variant="body2" sx={{ px: 2, pt: 2 }}>
            {translate('popular_specialties')}
          </Typography>
          <Grid sx={{ ...(theme?.custom?.SearchBar?.resultsList ?? {}) }}>
            {bubbleView ? (
              <Box display="contents">
                {Object.keys(groupedSpecialtiesByFirstLetter).map((key, index) => (
                  <Box display="contents" key={index}>
                    <Box style={{ marginTop: '8px' }}>{key}</Box>
                    {groupedSpecialtiesByFirstLetter[key].map((item) => (
                      <ResultsListItem
                        key={`search-${item.subtitle}-${item.id}`}
                        specialistOrPractice={specialistOrPractice}
                        searchString={searchString}
                        {...item}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            ) : (
              specialties.map((item: Item) => (
                <ResultsListItem
                  key={`search-${item.subtitle}-${item.id}`}
                  specialistOrPractice={specialistOrPractice}
                  searchString={searchString}
                  {...item}
                />
              ))
            )}
          </Grid>
        </>
      ) : null}
    </RootContainer>
  )
}

export default ResultsList
